/**
 * Glyph coverage check for the palette font stack.
 *
 * The chips render `s¹`, `ṭ`, `ʿ`, `ḏ`, `ẓ` and the modifier letters U+02BE / U+02BF. If the
 * browser's chosen font lacks any of them the annotator sees tofu boxes in the one place they
 * are supposed to be reading letters, which makes the tool unusable — and, worse, it is the
 * kind of failure someone works around by guessing rather than reporting.
 *
 * So the app measures it at startup instead of trusting a CSS font stack: each character is
 * measured against U+E000, a Private Use codepoint no font maps, which therefore renders as
 * .notdef. Equal advance widths is strong evidence the character is also falling back to
 * .notdef. It is a heuristic — a font whose real glyph happens to have the .notdef width would
 * be a false positive — so the result is reported as a warning, never used to change behaviour.
 */

/** Everything a chip or the palette can display. Kept in sync with `vocab.json` displays. */
export const GLYPH_SAMPLES = [
  'ʾ',
  'ʿ',
  'ḏ',
  'ḍ',
  'ġ',
  'ḥ',
  'ḫ',
  'ṣ',
  'ṭ',
  'ṯ',
  'ẓ',
  's¹',
  's²',
] as const;

/** U+E000: Private Use Area. No font maps it, so it renders as .notdef. */
const NOTDEF = '\uE000';

export interface FontCheckResult {
  font: string;
  missing: string[];
  checked: number;
  /** True when the check could not run (no canvas, e.g. a headless test environment). */
  skipped: boolean;
}

/**
 * Measure every sample against .notdef in `font`.
 *
 * `font` is a CSS shorthand, e.g. `24px "Charis SIL", "DejaVu Sans", serif`.
 */
export function checkGlyphCoverage(font: string): FontCheckResult {
  const canvas =
    typeof document === 'undefined' ? null : document.createElement('canvas');
  const ctx = canvas?.getContext('2d') ?? null;
  if (!ctx) {
    return { font, missing: [], checked: 0, skipped: true };
  }
  ctx.font = font;
  const notdefWidth = ctx.measureText(NOTDEF).width;
  const missing: string[] = [];
  for (const sample of GLYPH_SAMPLES) {
    // Compare per code point: 's¹' is two characters, and only the superscript can go missing.
    for (const char of sample) {
      const width = ctx.measureText(char).width;
      if (Math.abs(width - notdefWidth) < 0.01 && width > 0) {
        missing.push(char);
      }
    }
  }
  return { font, missing: [...new Set(missing)], checked: GLYPH_SAMPLES.length, skipped: false };
}

/** The stack the chips use. Kept here so the check and the CSS cannot drift apart. */
export const PALETTE_FONT_STACK =
  '"Charis SIL", "Gentium Plus", "Doulos SIL", "Brill", "Noto Serif", "DejaVu Serif", "DejaVu Sans", "Segoe UI", "Lucida Sans Unicode", "Arial Unicode MS", serif';

export function checkPaletteFont(sizePx = 22): FontCheckResult {
  return checkGlyphCoverage(`${sizePx}px ${PALETTE_FONT_STACK}`);
}
